import {
  Action,
  Dispatch,
  ThunkDispatch,
  configureStore,
} from "@reduxjs/toolkit";
import { PersistConfig, persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { upsellingApiSlice } from "../http/getUpsellingItems";
import { cartMiddleware } from "../middleware/cartActionV2Middleware";
import layoutReducer from "../slices/LayoutSlice";
import cartAndLoyaltyReducer from "../slices/cartAndLoyaltySlice";
import cartLoyaltyV2Reducer from "../slices/cartLoyaltyV2Slice";
import configReducer from "../slices/configSlice";
import deliveryAddress from "../slices/deliverySlice";
import drawersReducer from "../slices/drawers";
import franchiseStatusReducer from "../slices/franchiseStatusSlice";
import globalComponentsToggleSlice from "../slices/globalComponentsToggleSlice";
import itemStatusReducer from "../slices/itemStatusSlice";
import locationReducer from "../slices/locationSlice";
import loyaltyCustomerInfoReducer from "../slices/loyaltyCustomerInfoSlice";
import loyaltyTierSystemReducer from "../slices/loyaltyTierSystemSlice";
import modalReducer from "../slices/modalSlice";
import notificationReducer from "../slices/notificationsSlice";
import partnerAccountReducer from "../slices/partnerAccountSlice";
import receiptReducer from "../slices/receiptSlice";
import referenceReducer from "../slices/referenceSlice";
import requestLimiterSlice from "../slices/requestLimiterSlice";
import queryParamsReducer from "../slices/urlParamsSlice";
import userInfoReducer from "../slices/userInfoSlice";
import { TransformOfferType } from "../types/TypeHelper";
import { customerShortIdExpireTransform } from "./customerShortIdExpire";
import { purgeStaleRestaurantPersistedState } from "./stalePersistPurge";
import { doubleLinkedListTransform, loadingResetTransform } from "./transform";
import { upSellingExpireTransform } from "./upSellingSliceExpire";

// Create a transform to reset loading states on rehydration
import { CartLoyaltyV2SliceType } from "../types/CartLoyaltyV2Types";

// Drop persisted state written by a build for a different restaurant id.
// Must run before persistStore() below kicks off rehydration.
purgeStaleRestaurantPersistedState();

const persistConfig = {
  key: "root",
  storage,
};
const persistConfig2 = {
  key: "config",
  storage,
  blacklist: [
    "shouldClearCartDueToRestaurantSwitch",
    "isRestaurantSwitchInProgress",
  ],
};
const persistConfigDelivery = {
  key: "delivery",
  storage,
};
const persistConfigItemStatus = {
  key: "itemStatus",
  storage,
};
const persistReference = {
  key: "reference",
  storage,
};
const persistPartnerAccount = {
  key: "partnerAccount",
  storage,
};
const persistUserInfo = {
  key: "userInfo",
  storage,
  transforms: [customerShortIdExpireTransform],
};

const persistQueryParams = {
  key: "queryParams",
  storage,
};

const persistLocation = {
  key: "location",
  storage,
};

const persistCartLoyaltyV2: PersistConfig<CartLoyaltyV2SliceType> = {
  key: "cartLoyaltyV2",
  storage,
  transforms: [loadingResetTransform, doubleLinkedListTransform],
};

const persistFranchiseStatus = {
  key: "franchiseStatus",
  storage,
  whitelist: ["selfService", "selfServiceWithoutSignIn"],
};

const persistReceipt = {
  key: "receipt",
  storage,
  whitelist: ["deliveryReceipt", "posPayType", "currentReceiptId"],
};

const persistUpSellingItems = {
  key: "upsellingApiSlice",
  storage,
  whitelist: ["queries", "mutations"],
  transforms: [upSellingExpireTransform],
};

const persistedLocation = persistReducer(persistLocation, locationReducer);

const persistedCartLoyaltyV2 = persistReducer(
  persistCartLoyaltyV2,
  cartLoyaltyV2Reducer
);

const persistedUserInfo = persistReducer(persistUserInfo, userInfoReducer);
const persistedQueryParams = persistReducer(
  persistQueryParams,
  queryParamsReducer
);

const persistedcartAndLoyaltyReducer = persistReducer(
  persistConfig,
  cartAndLoyaltyReducer
);

const persistedPartnerAccount = persistReducer(
  persistPartnerAccount,
  partnerAccountReducer
);

const persistedReference = persistReducer(persistReference, referenceReducer);

// const persistedPaymentReducer = persistReducer(persistPayment, paymentReducer);
const persistedConfig = persistReducer(persistConfig2, configReducer);
const persistedDelivery = persistReducer(
  persistConfigDelivery,
  deliveryAddress
);
const persistedItemStatus = persistReducer(
  persistConfigItemStatus,
  itemStatusReducer
);

const persistedFranchiseStatus = persistReducer(
  persistFranchiseStatus,
  franchiseStatusReducer
);

const presistedReceipt = persistReducer(persistReceipt, receiptReducer);

const persistedUpSellingItems = persistReducer(
  persistUpSellingItems,
  upsellingApiSlice.reducer
);

export const store = configureStore({
  reducer: {
    config: persistedConfig,
    franchiseStatus: persistedFranchiseStatus,
    globalComponentsToggleSlice: globalComponentsToggleSlice,
    delivery: persistedDelivery,
    cartAndLoyaltyReducer: persistedcartAndLoyaltyReducer,
    userInfo: persistedUserInfo,
    queryParams: persistedQueryParams,
    layout: layoutReducer,
    drawersSlice: drawersReducer,
    requestLimiter: requestLimiterSlice,
    partnerAccount: persistedPartnerAccount,
    reference: persistedReference,
    receipt: presistedReceipt,
    itemStatus: persistedItemStatus,
    notifications: notificationReducer,
    modal: modalReducer,
    location: persistedLocation,
    cartLoyaltyV2Reducer: persistedCartLoyaltyV2,
    loyaltyCustomerInfo: loyaltyCustomerInfoReducer,
    loyaltyTierSystem: loyaltyTierSystemReducer,
    [upsellingApiSlice.reducerPath]: persistedUpSellingItems,
  },
  // Dev-only serializable/immutable checks stay off (parity with the pre-RTK2
  // hand-rolled middleware array): this store deliberately keeps Offer class
  // instances in actions and state (redeemOfferAction payloads, offersArray),
  // and redux-persist dispatches PERSIST/REHYDRATE — the checks would flood
  // the dev console and deep-walk the large menu state on every action.
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }).concat(cartMiddleware, upsellingApiSlice.middleware),
});

export const waitForStatePersistence = () => {
  return new Promise<void>((resolve) => {
    persistStore(store, null, () => {
      resolve();
    });
  });
};

// ts-prune-ignore-next
export const persistor = persistStore(store);

type RootStateBase = Omit<
  ReturnType<typeof store.getState>,
  "cartAndLoyaltyReducer"
>;

export type RootState = RootStateBase & {
  cartAndLoyaltyReducer: TransformOfferType<
    ReturnType<typeof store.getState>["cartAndLoyaltyReducer"]
  >;
};

// Not derived from `typeof store.dispatch`: the store's own dispatch is typed
// against the raw persisted state, which is incompatible with the
// TransformOfferType-mapped RootState used by hand-written thunks.
export type AppDispatch = Dispatch<Action> &
  ThunkDispatch<RootState, unknown, Action>;
